import * as React from 'react';
import type { CSSProperties, FC, MouseEvent } from 'react';
import { useState } from 'react';
import { translateHtmlEntities } from 'app/helpers/strings';
import type { Game, GameCover } from 'app/CasinoGames/types';
import iconPlay from './images/play_icon.svg';
import type { GameStarter } from '../useGameStarter';
import Favorites from '../Favorites';
import gamePath from '../gamePath';

// The tile's width in each column layout of .xo-games_item (_games.scss): the viewport less the
// $rs-gutter padding (2 x 10px), $xo-sidebar-width (290px) from 1025px and the gaps; .rs-row_x-large
// stops the grid growing at 1606px. Keep in step with those styles.
const COVER_SIZES = [
  '(max-width: 499px) calc(50vw - 14px)',
  '(max-width: 640px) calc(33.3vw - 12px)',
  '(max-width: 767px) calc(33.3vw - 17px)',
  '(max-width: 939px) calc(25vw - 17px)',
  '(max-width: 1024px) calc(20vw - 17px)',
  '(max-width: 1199px) calc(33.3vw - 114px)',
  '(max-width: 1459px) calc(25vw - 90px)',
  '(max-width: 1605px) calc(20vw - 75px)',
  '247px',
].join(', ');

const placeholderStyle = ({ placeholder }: GameCover): CSSProperties | undefined => {
  if (!placeholder) return undefined;

  const colors = Object.fromEntries(placeholder.colors.map((color, index) => [`--p${index}`, color]));
  return { '--ph': `url(data:image/webp;base64,${placeholder.webp})`, ...colors } as CSSProperties;
};

const GameComponent: FC<{
  game: Game;
  playerPresent: boolean;
  gameStarter: GameStarter;
}> = ({ game, playerPresent, gameStarter }) => {
  const { cover } = game;
  // Always show texts if image is absent
  const [hoveredClassName, setHoveredClassName] = useState(cover ? '' : 'hovered');

  const gameLabelId = `game-label-${game.id}`;

  const onMouseEnter = () => {
    if (cover) setHoveredClassName('hovered');
  };

  const onMouseLeave = () => {
    if (cover) setHoveredClassName('');
  };

  const handleClick = () => {
    if (playerPresent) {
      gameStarter.openRealGame(game);
      return;
    }

    if (game.demo) {
      gameStarter.openPracticeGame(game);
      return;
    }

    window.location.assign('/login');
  };

  // The name links to the game page for crawlers and for opening it in a new tab; a plain click
  // still opens the game inline.
  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    handleClick();
  };

  const path = gamePath(game);
  const name = (
    <span className="xo-game_name" id={gameLabelId}>
      {translateHtmlEntities(game.gameLocaleName)}
    </span>
  );

  // Server-rendered and lazy, so the browser fetches the cover without waiting for the bundle. The
  // layout's cover_fade.js marks it loaded (data-loaded), also before hydration.
  const image = (gameCover: GameCover) => (
    <div className="xo-game_image" style={placeholderStyle(gameCover)}>
      <picture>
        {gameCover.webp && <source type="image/webp" srcSet={gameCover.webp} sizes={COVER_SIZES} />}
        <img
          loading="lazy"
          decoding="async"
          srcSet={gameCover.jpg}
          sizes={gameCover.jpg && COVER_SIZES}
          src={gameCover.src}
          alt=""
          className="js-game-cover"
          suppressHydrationWarning
        />
      </picture>
    </div>
  );

  return (
    <div
      className={`xo-games_item game_container ${hoveredClassName}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-labelledby={gameLabelId}
    >
      <div className={`xo-game ${game.gameClassName}`}>
        {cover && image(cover)}
        <button type="button" onClick={handleClick} className="xo-game_button__mobile" aria-label="Play mobile" />
        <div className="xo-game_info">
          {playerPresent && <Favorites game={game} />}
          <div className={`xo-game_name-wrap ${playerPresent ? 'logged-in' : ''}`}>
            {path ? (
              <a href={path} onClick={handleLinkClick} className="xo-game_link">
                {name}
              </a>
            ) : (
              name
            )}
          </div>
          <button type="button" onClick={handleClick} className="xo-game_button">
            <img src={iconPlay} alt="Play" />
          </button>
          <span className="xo-game_vendor">{game.vendor}</span>
        </div>
      </div>
    </div>
  );
};

export default GameComponent;
